// Board app: wires the store, the renderer and the DOM chrome (top bar, tool rail, zoom, menus) together
// and turns pointer and keyboard input into board operations.
import { MAX_ZOOM, MIN_ZOOM, fit, toBoard, toScreen, zoomAt, type Cam } from "./camera";
import { frameSections, SECTION_TAB } from "./sections";
import { INK_COLORS, INK_SIZES, INK_TOOLS, MAX_INK_POINTS, inkWidth, loadInk, saveInk, strokeHit } from "./ink";
import type { LinkKind, Status } from "./engine-api";
import { DetailsPanel } from "./details";
import { Minimap } from "./minimap";
import { FilterBar } from "./filterbar";
import { FrameEditor, defaultLayout, tableLayout } from "./frame-editor";
import { DOMAINS } from "./templates";
import { formatPoints, isoWeek } from "./iso";
import { distance, handlePoint, resizePoint } from "./links";
import { LINK_COLORS, STATUS, STICKY, css, personColor } from "./palette";
import type { Presence, View } from "./presence";
import { BoardRenderer, type RenderState } from "./renderer";
import { TimerWidget } from "./timer";
import { ImageCache, fileUrl, isImage, placeSize, prepare, upload } from "./images";
import { ViewList } from "./views";
import type { Store } from "./store";
import type { SyncClient, SyncStatus } from "./sync";

const $ = <T extends HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

/** The three pads of the dock (C1): colour + preset owner, set per browser via right click. */
interface Pad { color: number; owner: string }
type Tool = "select" | "lasso" | "draw";
const PADS_KEY = "lanewerk.pads";
const DEFAULT_PADS: Pad[] = [{ color: 0, owner: "" }, { color: 5, owner: "" }, { color: 3, owner: "" }]; // Gelb, Blau, Limette
function loadPads(): Pad[] {
  try {
    const v = JSON.parse(localStorage.getItem(PADS_KEY) ?? "null") as Pad[] | null;
    if (Array.isArray(v) && v.length === 3) {
      return v.map((p) => ({ color: Math.min(11, Math.max(0, Number(p.color) | 0)), owner: String(p.owner ?? "").slice(0, 4).toUpperCase() }));
    }
  } catch { /* storage blocked */ }
  return DEFAULT_PADS.map((p) => ({ ...p }));
}
/** Clipboard format of copied cards (JSON clip, see lanewerk-core board/clip.rs). */
const CLIP_TYPE = "application/x-lanewerk-cards";
/** Duplicates sit a little left and below: their top-right corner stays in the same cell and week. */
const DUP_DX = -16, DUP_DY = 16;

type Mode =
  | { kind: "idle" }
  | { kind: "pan"; sx: number; sy: number; cam: Cam; moved: boolean }
  | { kind: "drag"; bx: number; by: number; moved: boolean }
  | { kind: "lasso"; bx: number; by: number; x: number; y: number }
  | { kind: "link"; from: string }
  | { kind: "resize"; id: string; right: number; top: number }
  | { kind: "image"; id: string; bx: number; by: number; moved: boolean }
  | { kind: "imageResize"; id: string; x: number; y: number; ratio: number }
  | { kind: "frame"; id: string; bx: number; by: number; moved: boolean }
  | { kind: "ink" }
  | { kind: "erase" }
  | { kind: "inkDrag"; bx: number; by: number; moved: boolean };

export class App {
  cam: Cam = { x: -100, y: -100, z: 1 };
  selection = new Set<string>();
  selectedLink: string | null = null;
  private drag: RenderState["drag"] = null;
  private resizing: RenderState["resize"] = null;
  /** Images (H1): the selected one, a drag or a resize in progress. */
  selectedImage: string | null = null;
  private imageDrag: RenderState["imageDrag"] = null;
  private imageResizing: RenderState["imageResize"] = null;
  private imageCache: ImageCache;
  private linkDraft: RenderState["linkDraft"] = null;
  selectedFrame: string | null = null;
  private frameDrag: RenderState["frameDrag"] = null;
  private frameEditor: FrameEditor;
  readonly details: DetailsPanel;
  private minimap: Minimap;
  private editing: string | null = null;
  private mode: Mode = { kind: "idle" };
  private renderer: BoardRenderer;
  private canvas: HTMLCanvasElement;
  private stage: HTMLElement;
  private frameRequested = false;
  sync: SyncClient | null = null;
  readonly filters: FilterBar;
  private presence: Presence | null = null;
  private timer: TimerWidget;
  private views: ViewList;
  /** Presentation mode (E3): the presenter this viewer follows, one it chose not to follow. */
  private following: number | null = null;
  private declined: number | null = null;
  private followed = "";
  private presentAction: (() => void) | null = null;
  private readOnly = false;
  /** The last pointer was a finger or a pen (B7): grips, handles and links are easier to hit. */
  private coarse = false;
  private lastPointerType = "mouse";
  /** Drawings: the drawing bar's settings, the stroke being drawn, selected strokes, their drag
   *  offset, strokes the eraser has touched; once a pencil drew, fingers only move the view. */
  private ink = loadInk();
  private inkDraft: { pts: number[]; color: number; kind: number } | null = null;
  private selectedStrokes = new Set<string>();
  private inkDrag: { dx: number; dy: number } | null = null;
  private inkErase = new Set<string>();
  private penSeen = false;
  /** Last pointer position on the board (paste target); null when the pointer is elsewhere. */
  private pointer: { x: number; y: number } | null = null;
  private boardId: string;

  constructor(private store: Store, boardId: string, font: string) {
    this.stage = $("#lw-stage");
    this.canvas = $("#lw-canvas");
    this.renderer = new BoardRenderer(this.canvas, font);
    this.boardId = boardId;
    this.bindTitle(boardId);
    this.buildDock();
    this.filters = new FilterBar(store, (r) => this.fitTo(r, 1.5), () => this.canvas.focus({ preventScroll: true }));
    this.frameEditor = new FrameEditor(() => this.canvas.focus({ preventScroll: true }));
    this.details = new DetailsPanel(store, () => this.canvas.focus({ preventScroll: true }), boardId);
    this.timer = new TimerWidget(store);
    this.imageCache = new ImageCache(boardId, () => this.requestRender());
    this.renderer.imageSource = (f) => this.imageCache.get(f);
    // someone's chosen colour arrived or changed: avatars and card headers follow
    addEventListener("lanewerk:colors", () => { this.renderPeople(); this.requestRender(); });
    this.minimap = new Minimap(
      store,
      () => { const r = this.stage.getBoundingClientRect(); return { cam: this.cam, w: r.width, h: r.height }; },
      (bx, by) => { const r = this.stage.getBoundingClientRect(); this.setCam({ ...this.cam, x: bx - r.width / (2 * this.cam.z), y: by - r.height / (2 * this.cam.z) }); },
    );
    this.views = new ViewList(store, () => this.viewRect(), (r) => this.showRect(r));
    this.bindChrome();
    this.bindInput();
    this.bindClipboard();
    new ResizeObserver(() => this.resize()).observe(this.stage);
    this.resize();
    store.subscribe(() => {
      // drop selected cards that no longer exist (deleted here or elsewhere)
      for (const id of this.selection) if (!store.card(id)) this.selection.delete(id);
      if (this.selectedLink && !store.link(this.selectedLink)) this.selectedLink = null;
      if (this.selectedFrame && !store.frames().some((f) => f.id === this.selectedFrame)) this.selectedFrame = null;
      this.updateChrome();
      this.requestRender();
    });
    this.updateChrome();
  }

  // ---------- rendering ----------

  requestRender() {
    if (this.frameRequested) return;
    this.frameRequested = true;
    requestAnimationFrame(() => {
      this.frameRequested = false;
      const handle = this.selection.size === 1 && !this.selectedLink && !this.readOnly ? [...this.selection][0] : null;
      this.renderer.render(this.store, {
        cam: this.cam, selection: this.selection, drag: this.drag, editing: this.editing,
        selectedLink: this.selectedLink, handle, resize: this.resizing, linkDraft: this.linkDraft,
        frameDrag: this.frameDrag, selectedFrame: this.selectedFrame,
        selectedImage: this.selectedImage, imageDrag: this.imageDrag, imageResize: this.imageResizing,
        ink: { draft: this.inkDraft, selected: this.selectedStrokes, drag: this.inkDrag, hidden: this.inkErase },
      });
      this.positionEditor();
      this.renderCursors();
      $("#lw-zoom-level").textContent = `${Math.round(this.cam.z * 100)} %`;
      this.minimap.draw();
    });
  }

  private resize() {
    const r = this.stage.getBoundingClientRect();
    this.renderer.resize(r.width, r.height, Math.min(window.devicePixelRatio || 1, 2));
    // presenting: the others see a new rect; following: fit the presenter's rect again
    this.followed = "";
    if (this.presence) { this.sendView(); this.updatePresentation(); }
    this.requestRender();
  }

  /** Board map on/off (kept per browser). */
  toggleMap(on?: boolean) {
    this.minimap.toggle(on);
    $("#lw-map-toggle").setAttribute("aria-pressed", String(this.minimap.open));
    try { localStorage.setItem("lanewerk.map", this.minimap.open ? "1" : "0"); } catch { /* fine */ }
    this.requestRender();
  }

  /** Gives the keyboard back to the board. */
  focus() { this.canvas.focus({ preventScroll: true }); }

  /** Closes the side panels (frame editor, card details) – one panel at a time. */
  closePanels() {
    this.frameEditor.close();
    this.details.close();
  }

  /** Card details panel (description, link, …). */
  openDetails(id: string) {
    if (!this.store.card(id)) return;
    this.commitEditor();
    this.closeMenu();
    this.frameEditor.close();
    document.getElementById("lw-share-panel")?.setAttribute("hidden", "");
    this.selection = new Set([id]);
    this.selectedLink = null;
    this.selectedFrame = null;
    this.details.open(id);
    this.requestRender();
  }

  fitToContent() {
    const b = this.store.bounds();
    if (b) this.fitTo(b);
  }

  /** Selects a card and brings it into view (deep link from "Meine Aufgaben"). */
  focusCard(id: string) {
    const c = this.store.card(id);
    if (!c) return false;
    this.selection = new Set([id]);
    this.selectedLink = null;
    this.fitTo([c.x - 300, c.y - 200, c.w + 600, c.h + 400], 1.2);
    return true;
  }

  /** Shows exactly this board rect (a saved view area): as large as the screen allows. */
  showRect(r: [number, number, number, number]) {
    const s = this.stage.getBoundingClientRect();
    this.setCam(fit(r, s.width, s.height, 0));
  }

  /** Shows a board rect, zooming in no further than `maxZoom` (a single card should not fill the screen). */
  fitTo(b: [number, number, number, number], maxZoom = Infinity) {
    const r = this.stage.getBoundingClientRect();
    let c = fit(b, r.width, r.height);
    if (c.z > maxZoom) c = { x: b[0] + b[2] / 2 - r.width / (2 * maxZoom), y: b[1] + b[3] / 2 - r.height / (2 * maxZoom), z: maxZoom };
    this.setCam(c);
  }

  /** `follow`: set by the presentation the viewer follows (any other camera move stops following). */
  private setCam(c: Cam, follow = false) {
    this.cam = c;
    if (!follow) {
      if (this.following !== null) { this.unfollow(); this.updatePresentation(); }
      this.closeMenu();
    }
    this.sendView();
    this.requestRender();
  }

  // ---------- presentation (E3) ----------

  /** The board rect this viewer sees. */
  private viewRect(): View {
    const r = this.stage.getBoundingClientRect();
    return [this.cam.x, this.cam.y, r.width / this.cam.z, r.height / this.cam.z];
  }

  private sendView() {
    if (this.presence?.presenting) this.presence.setView(this.viewRect());
  }

  get isFollowing() { return this.following !== null; }

  togglePresenting() {
    const p = this.presence;
    if (!p) return;
    if (p.presenting) p.setView(null);
    else {
      if (this.following !== null) this.unfollow();
      p.setView(this.viewRect());
    }
    this.updatePresentation();
  }

  private followPeer(id: number) {
    this.following = id;
    this.declined = null;
    this.followed = "";
    this.presence?.setFollow(id);
  }

  /** Stop following; `decline`: no automatic follow of this presenter until they present anew. */
  private unfollow(decline = true) {
    if (decline) this.declined = this.following;
    this.following = null;
    this.presence?.setFollow(null);
  }

  /** After every presence change: follow, move the camera, update banner and button. */
  private updatePresentation() {
    const p = this.presence;
    if (!p) return;
    const presenters = [...p.peers.values()].filter((q) => q.view).sort((a, b) => a.id - b.id);
    if (this.following !== null && !presenters.some((q) => q.id === this.following)) this.unfollow(false);
    if (this.declined !== null && !presenters.some((q) => q.id === this.declined)) this.declined = null;
    // a presentation starts: everyone follows it, except the ones presenting or who chose not to
    if (this.following === null && !p.presenting) {
      const next = presenters.find((q) => q.id !== this.declined);
      if (next) this.followPeer(next.id);
    }
    const lead = this.following !== null ? p.peers.get(this.following) : undefined;
    if (lead?.view) {
      const key = lead.view.join(",");
      if (key !== this.followed) {
        this.followed = key;
        const r = this.stage.getBoundingClientRect();
        this.setCam(fit(lead.view, r.width, r.height, 0), true);
      }
    }
    const btn = $("#lw-present-toggle");
    btn.setAttribute("aria-pressed", String(p.presenting));
    btn.title = p.presenting ? "Präsentation beenden" : "Alle anderen auf diesem Board sehen, was du siehst";
    this.stage.classList.toggle("presenting", p.presenting);
    this.stage.classList.toggle("following", this.following !== null);
    let text = "", label = "";
    this.presentAction = null;
    if (p.presenting) {
      const n = [...p.peers.values()].filter((q) => q.follow === p.peerId).length;
      text = `Du präsentierst · ${n === 1 ? "1 Person folgt" : `${n} Personen folgen`}`;
      label = "Beenden";
      this.presentAction = () => this.togglePresenting();
    } else if (lead) {
      text = `Du folgst ${lead.name}`;
      label = "Nicht mehr folgen";
      this.presentAction = () => { this.unfollow(); this.updatePresentation(); };
    } else if (presenters.length) {
      const q = presenters[0];
      text = `${q.name} präsentiert`;
      label = "Folgen";
      this.presentAction = () => { this.followPeer(q.id); this.updatePresentation(); };
    }
    const box = $("#lw-present");
    box.hidden = !text;
    $("#lw-present-text").textContent = text;
    $("#lw-present-action").textContent = label;
  }

  // ---------- presence ----------

  /** Connects the presence of the others (cursors, avatars); the own name lives in the account menu. */
  attachPresence(p: Presence) {
    this.presence = p;
    const canvas = this.canvas;
    canvas.addEventListener("pointermove", (e) => {
      const { sx, sy } = this.local(e);
      p.setCursor(toBoard(this.cam, sx, sy));
    });
    canvas.addEventListener("pointerleave", () => p.setCursor(null));
    p.subscribe(() => { this.renderPeople(); this.updatePresentation(); this.requestRender(); });
    $("#lw-present-toggle").hidden = false;
    $("#lw-present-toggle").addEventListener("click", () => this.togglePresenting());
    $("#lw-present-action").addEventListener("click", () => this.presentAction?.());
    this.renderPeople();
    this.updatePresentation();
  }

  private renderPeople() {
    const p = this.presence;
    if (!p) return;
    const box = $("#lw-people");
    const peers = [...p.peers.values()].sort((a, b) => a.name.localeCompare(b.name, "de"));
    const shown = peers.slice(0, 5);
    box.replaceChildren(...shown.map((q) => {
      const a = el("span", "avatar", q.short || "?");
      a.style.background = css(personColor(q.short || q.name));
      a.title = q.view ? `${q.name} – präsentiert` : q.name;
      if (q.view) a.classList.add("presenter");
      return a;
    }));
    if (peers.length > shown.length) {
      const more = el("span", "avatar more", `+${peers.length - shown.length}`);
      more.title = peers.slice(5).map((q) => q.name).join(", ");
      box.appendChild(more);
    }
    box.setAttribute("aria-label", peers.length ? `Gerade auf diesem Board: ${peers.map((q) => q.name).join(", ")}` : "Sonst niemand auf diesem Board");
  }

  /** Pointer of every other viewer, positioned in screen space on each frame. */
  private renderCursors() {
    const p = this.presence;
    const box = $("#lw-cursors");
    if (!p) return;
    const seen = new Set<string>();
    for (const q of p.peers.values()) {
      if (q.x === null || q.y === null) continue;
      const key = String(q.id);
      seen.add(key);
      let c = box.querySelector<HTMLElement>(`[data-peer="${key}"]`);
      const color = css(personColor(q.short || q.name));
      if (!c) {
        c = el("div", "cursor");
        c.dataset.peer = key;
        c.innerHTML = `<svg viewBox="0 0 16 18"><path d="M1 1l13 7-6 1.5L5 16z" stroke="#fff" stroke-width="1.2" stroke-linejoin="round"/></svg><span class="cursor-label"></span>`;
        box.appendChild(c);
      }
      (c.querySelector("path") as SVGPathElement).setAttribute("fill", color);
      const label = c.querySelector<HTMLElement>(".cursor-label")!;
      label.textContent = q.name;
      label.style.background = color;
      const s = toScreen(this.cam, q.x, q.y);
      c.style.transform = `translate(${Math.round(s.x)}px, ${Math.round(s.y)}px)`;
    }
    for (const c of [...box.children] as HTMLElement[]) if (!seen.has(c.dataset.peer ?? "")) c.remove();
  }

  // ---------- chrome ----------

  /** Readers see the board but cannot change it (role from the server, see sync.ts). */
  setReadOnly(ro: boolean) {
    this.readOnly = ro;
    this.store.readOnly = ro;
    document.body.classList.toggle("is-readonly", ro);
    $("#lw-readonly").hidden = !ro;
    if (ro) {
      this.commitEditor(false);
      this.closeMenu();
      this.frameEditor.close();
      this.linkDraft = null;
      this.mode = { kind: "idle" };
    }
    this.details.setReadOnly(ro);
    this.timer.render();
    this.views.render();
    this.updateChrome();
    this.requestRender();
  }

  setSyncStatus(s: SyncStatus) {
    const chip = $("#lw-sync");
    chip.dataset.state = s;
    chip.textContent = s === "online" ? "Verbunden" : s === "connecting" ? "Verbinde …" : "Offline – Änderungen bleiben lokal";
  }

  /** Board title in the top bar: click to rename (Enter saves, Esc cancels). */
  private bindTitle(boardId: string) {
    const btn = $<HTMLButtonElement>("#lw-board-name");
    const input = $<HTMLInputElement>("#lw-board-title");
    const show = () => {
      const t = this.store.boardTitle();
      btn.textContent = t || boardId;
      btn.classList.toggle("unnamed", !t);
      document.title = `${t || boardId} – Lanewerk`;
    };
    let editing = false;
    const close = (save: boolean) => {
      if (!editing) return;
      editing = false;
      const v = input.value.trim();
      input.hidden = true;
      btn.hidden = false;
      if (save && v && v !== this.store.boardTitle()) this.store.setBoardTitle(v);
      this.canvas.focus({ preventScroll: true });
    };
    btn.addEventListener("click", () => {
      if (this.readOnly) return;
      editing = true;
      input.value = this.store.boardTitle() || "";
      input.placeholder = boardId;
      btn.hidden = true;
      input.hidden = false;
      input.focus();
      input.select();
    });
    input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") { e.preventDefault(); close(true); }
      else if (e.key === "Escape") { e.preventDefault(); close(false); }
    });
    input.addEventListener("blur", () => close(true));
    this.store.subscribe(show);
    show();
  }

  private updateChrome() {
    const c = this.store.counts();
    $("#lw-count-open").textContent = String(c.open);
    $("#lw-count-done").textContent = String(c.done);
    $("#lw-count-waiting").textContent = String(c.waiting);
    $("#lw-count-cancelled").textContent = String(c.cancelled);
    $("#lw-count-total").textContent = String(c.open + c.done + c.waiting + c.cancelled);
    // progress (D4): done share of everything not cancelled – by story points when there are any
    const p = c.points;
    const pTotal = p ? p.open + p.done + p.waiting : 0;
    const byPoints = pTotal > 0;
    const [doneN, totalN] = byPoints ? [p!.done, pTotal] : [c.done, c.open + c.done + c.waiting];
    const progress = $("#lw-progress");
    progress.hidden = totalN === 0;
    $("#lw-count-progress").textContent = totalN ? `${Math.round((doneN / totalN) * 100)} %` : "";
    progress.title = byPoints
      ? "Erledigte Story Points von allen nicht abgebrochenen Karten"
      : "Erledigte Karten von allen nicht abgebrochenen Karten";
    $("#lw-points").hidden = !byPoints;
    if (byPoints) $("#lw-count-points").textContent = `${formatPoints(p!.done)} / ${formatPoints(pTotal)}`;
    const conflicts = this.store.conflictCount();
    $("#lw-conflicts").hidden = conflicts === 0;
    $("#lw-count-conflicts").textContent = String(conflicts);
    $("#lw-archived").hidden = !this.store.boardArchived();
    const empty = this.store.frames().length === 0 && this.store.cardCount() === 0 && this.store.strokes().length === 0 && this.store.images().length === 0;
    // the welcome box sits in the middle of the board: not while drawing there
    $("#lw-empty").hidden = !empty || this.readOnly || this.tool === "draw";
  }

  private bindChrome() {
    $("#lw-export").addEventListener("click", () => void this.exportExcel());
    $("#lw-undo").addEventListener("click", () => this.store.undo());
    $("#lw-redo").addEventListener("click", () => this.store.redo());
    $("#lw-add-frame").addEventListener("click", () => this.newFrameDialog());
    $("#lw-empty-frame").addEventListener("click", () => {
      this.addFrame(0, 0);
      this.fitToContent();
    });
    const zoomBy = (f: number) => {
      const r = this.stage.getBoundingClientRect();
      this.setCam(zoomAt(this.cam, r.width / 2, r.height / 2, f));
    };
    $("#lw-zoom-in").addEventListener("click", () => zoomBy(1.25));
    $("#lw-zoom-out").addEventListener("click", () => zoomBy(0.8));
    $("#lw-zoom-fit").addEventListener("click", () => this.fitToContent());
    $("#lw-map-toggle").addEventListener("click", () => this.toggleMap());
    try { if (localStorage.getItem("lanewerk.map") === "1") this.toggleMap(true); } catch { /* fine */ }
    const fs = $("#lw-fullscreen");
    fs.addEventListener("click", () => {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen?.().catch(() => {});
    });
    document.addEventListener("fullscreenchange", () => { fs.textContent = document.fullscreenElement ? "Vollbild aus" : "Vollbild"; });
    $("#lw-conflict-btn").addEventListener("click", () => {
      const b = this.store.conflictBounds();
      if (b) this.fitTo(b, 1.2);
    });
  }

  private addFrame(x: number, y: number) {
    const { year, week } = isoWeek(new Date());
    const id = `f${Date.now().toString(36)}`;
    this.store.addSprintFrame(id, "Sprint", x, y, year, week, 8, DOMAINS);
  }

  /** Frame editor for a new frame, placed near the top-left of the view. */
  private newFrameDialog() {
    this.details.close();
    const p = toBoard(this.cam, 40, 40);
    const id = `f${Date.now().toString(36)}`;
    const templates = [
      { name: "Sprint & Masterplan", layout: defaultLayout(DOMAINS) },
      { name: "Tabelle", layout: tableLayout() },
    ];
    this.frameEditor.open("Neuer Rahmen", templates[0].layout, "Anlegen", (l) => {
      const ok = this.store.addFrame(id, Math.round(p.x), Math.round(p.y), l);
      if (ok) this.selectedFrame = id;
      return ok;
    }, templates);
  }

  private editFrame(id: string) {
    this.details.close();
    const l = this.store.frameLayout(id);
    if (!l) return;
    this.frameEditor.open("Rahmen bearbeiten", l, "Übernehmen", (nl) => this.store.setFrameLayout(id, nl));
  }

  /** Frame whose header (title + date rows, or a section tab above them) lies under the board point; topmost first. */
  private frameHeaderAt(bx: number, by: number): string | null {
    const frames = this.store.frames();
    for (let i = frames.length - 1; i >= 0; i--) {
      const f = frames[i];
      const w = f.cols.reduce((a, c) => a + c.width, 0);
      let h = 0;
      for (const r of f.rows) { if (r.kind === "header" || r.kind === "subHeader") h += r.height; else break; }
      if (bx >= f.x && bx <= f.x + w && by >= f.y && by < f.y + h) return f.id;
      if (by >= f.y - SECTION_TAB && by < f.y && frameSections(f).some((s) => bx >= f.x + s.x0 && bx <= f.x + s.x1)) return f.id;
    }
    return null;
  }

  private frameRect(id: string): [number, number, number, number] | null {
    const f = this.store.frames().find((x) => x.id === id);
    if (!f) return null;
    return [f.x, f.y, f.cols.reduce((a, c) => a + c.width, 0), f.rows.reduce((a, r) => a + r.height, 0)];
  }

  private pads: Pad[] = loadPads();
  private railBound = false;
  /** Rail tool: "select" moves the view on the empty board, "lasso" draws a selection frame there. */
  private tool: Tool = "select";

  /** Tool rail on the left: the three favourite pads, the fan with all colours, select and lasso. */
  private buildDock() {
    $("#lw-pads").replaceChildren(...this.pads.map((pad, i) => {
      const b = el("button", "pad", pad.owner);
      b.type = "button";
      b.title = `Neue Karte (${STICKY[pad.color].name}${pad.owner ? `, ${pad.owner}` : ""}) – auf das Board ziehen oder antippen; Rechtsklick oder lange drücken: Farbe und Kürzel`;
      b.setAttribute("aria-label", b.title);
      b.style.background = css(STICKY[pad.color].rgb);
      b.addEventListener("pointerdown", (e) => { if (e.button === 0) this.startPadDrag(e, pad, () => this.openPadMenu(i, b, false)); });
      b.addEventListener("contextmenu", (e) => { e.preventDefault(); this.openPadMenu(i, b); });
      return b;
    }));
    if (this.railBound) return;
    this.railBound = true;
    const toggle = $("#lw-fan-toggle");
    toggle.addEventListener("click", () => (this.fanIsOpen() ? this.closeFan() : this.openFan()));
    document.querySelectorAll<HTMLButtonElement>("#lw-dock [data-tool]").forEach((t) => t.addEventListener("click", () => this.setTool(t.dataset.tool as Tool)));
    // a press anywhere else closes the fan
    document.addEventListener("pointerdown", (e) => {
      const t = e.target as Node;
      if (this.fanIsOpen() && !$("#lw-fan").contains(t) && !toggle.contains(t)) this.closeFan();
    });
  }

  private setTool(t: Tool) {
    this.tool = t;
    document.querySelectorAll<HTMLButtonElement>("#lw-dock [data-tool]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.tool === t)));
    this.canvas.style.cursor = t === "select" ? "default" : "crosshair";
    this.showInkBar(t === "draw");
    this.updateChrome();
  }

  private fanIsOpen() { return !$("#lw-fan").hidden; }

  /** All twelve note colours next to the rail: drag one onto the board, or tap it for a card in the middle. */
  private openFan() {
    this.closeMenu();
    const fan = $("#lw-fan");
    const grid = el("div", "fan-grid");
    STICKY.forEach((c, ci) => {
      const b = el("button", "fan-swatch");
      b.type = "button";
      b.title = `Neue Karte (${c.name}) – auf das Board ziehen oder antippen`;
      b.setAttribute("aria-label", `Neue Karte ${c.name}`);
      b.style.background = css(c.rgb);
      b.addEventListener("pointerdown", (e) => { if (e.button === 0) this.startPadDrag(e, { color: ci, owner: "" }); });
      grid.appendChild(b);
    });
    const sec = el("div", "menu-section");
    sec.append(el("div", "menu-title", "Alle Farben"), grid, el("div", "menu-hint", "Ziehen oder antippen. Lieblingsfarben in der Leiste: Rechtsklick oder lange drücken."));
    fan.replaceChildren(sec);
    fan.hidden = false;
    this.placeBesideRail(fan, $("#lw-fan-toggle"));
    $("#lw-fan-toggle").setAttribute("aria-expanded", "true");
  }

  private closeFan() {
    const fan = document.getElementById("lw-fan");
    if (fan) fan.hidden = true;
    document.getElementById("lw-fan-toggle")?.setAttribute("aria-expanded", "false");
  }

  /** A popover right of the rail, level with its anchor, kept inside the stage. */
  private placeBesideRail(pop: HTMLElement, anchor: HTMLElement) {
    const r = anchor.getBoundingClientRect(), rail = $("#lw-dock").getBoundingClientRect(), sr = this.stage.getBoundingClientRect();
    const y = Math.max(8, Math.min(r.top - sr.top - 12, sr.height - pop.offsetHeight - 8));
    pop.style.transform = `translate(${rail.right - sr.left + 10}px, ${y}px)`;
  }

  /** Colour and preset owner of a pad, kept in this browser. */
  private openPadMenu(i: number, anchor: HTMLElement, focus = true) {
    this.closeFan();
    const menu = $("#lw-menu");
    menu.replaceChildren();
    const pad = this.pads[i];
    const save = () => {
      try { localStorage.setItem(PADS_KEY, JSON.stringify(this.pads)); } catch { /* not kept */ }
      this.buildDock();
    };
    const sec = el("div", "menu-section");
    sec.appendChild(el("div", "menu-title", "Farbe des Blocks"));
    const grid = el("div", "swatches");
    STICKY.forEach((c, ci) => {
      const b = el("button", "swatch");
      b.type = "button";
      b.title = c.name;
      b.setAttribute("aria-label", c.name);
      b.style.background = css(c.rgb);
      if (pad.color === ci) b.setAttribute("aria-pressed", "true");
      b.addEventListener("click", () => { pad.color = ci; save(); this.closeMenu(); });
      grid.appendChild(b);
    });
    sec.appendChild(grid);
    menu.appendChild(sec);
    const who = el("div", "menu-section");
    who.appendChild(el("div", "menu-title", "Neue Karten gehören (Kürzel)"));
    const owner = el("input", "field");
    owner.maxLength = 4;
    owner.placeholder = "leer = niemand";
    owner.value = pad.owner;
    owner.setAttribute("aria-label", "Kürzel für neue Karten aus diesem Block");
    owner.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") { pad.owner = owner.value.trim().toUpperCase().slice(0, 4); save(); this.closeMenu(); }
      if (e.key === "Escape") this.closeMenu();
    });
    who.appendChild(owner);
    who.appendChild(el("div", "menu-hint", "Enter speichert. Gilt in diesem Browser für alle Boards."));
    menu.appendChild(who);
    menu.hidden = false;
    this.placeBesideRail(menu, anchor);
    if (focus) owner.focus(); // not after a long press: no keyboard popping up on a tablet
  }

  /** Drag a new card from a pad (rail or fan) onto the board; a tap puts it in the middle of the
   *  view. Touch and pen have no right click: holding a favourite pad opens its menu (`hold`). */
  private startPadDrag(e: PointerEvent, pad: Pad, hold?: () => void) {
    e.preventDefault();
    if (this.readOnly) return;
    const color = pad.color;
    const start = { x: e.clientX, y: e.clientY };
    const ghost = el("div", "ghost");
    ghost.style.background = css(STICKY[color].rgb);
    let dragged = false;
    const timer = hold && e.pointerType !== "mouse" ? window.setTimeout(() => { stop(); hold(); }, 500) : 0;
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== e.pointerId) return;
      if (!dragged && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < 4) return;
      if (!dragged) { dragged = true; window.clearTimeout(timer); document.body.appendChild(ghost); }
      const size = 100 * this.cam.z;
      ghost.style.width = ghost.style.height = `${size}px`;
      ghost.style.transform = `translate(${ev.clientX - size / 2}px, ${ev.clientY - size / 2}px)`;
    };
    const stop = () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      ghost.remove();
    };
    const cancel = (ev: PointerEvent) => { if (ev.pointerId === e.pointerId) stop(); };
    const up = (ev: PointerEvent) => {
      if (ev.pointerId !== e.pointerId) return;
      stop();
      if (!dragged) dragged = Math.hypot(ev.clientX - start.x, ev.clientY - start.y) >= 4;
      const r = this.stage.getBoundingClientRect();
      const inside = ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom;
      // a tap (no real drag) or a drop back onto the rail or the fan puts the card in the middle of
      // the view – not under them, where it would be hidden
      const under = document.elementFromPoint(ev.clientX, ev.clientY);
      const onRail = $("#lw-dock").contains(under) || $("#lw-fan").contains(under);
      const here = inside && dragged && !onRail;
      this.closeFan();
      const sx = here ? ev.clientX - r.left : r.width / 2;
      const sy = here ? ev.clientY - r.top : r.height / 2;
      const p = toBoard(this.cam, sx, sy);
      const id = this.store.createCard({ x: p.x - 50, y: p.y - 50, color, ...(pad.owner ? { owner: pad.owner } : {}) });
      this.selection = new Set([id]);
      this.openEditor(id);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
  }

  // ---------- input ----------

  private local(e: { clientX: number; clientY: number }) {
    const r = this.stage.getBoundingClientRect();
    return { sx: e.clientX - r.left, sy: e.clientY - r.top };
  }

  /** The single selected card whose connector handle is under the board point, if any. */
  private handleAt(bx: number, by: number): string | null {
    if (this.selection.size !== 1 || this.selectedLink || this.cam.z < 0.3) return null;
    const id = [...this.selection][0];
    const c = this.store.card(id);
    if (!c) return null;
    const [hx, hy] = handlePoint(c, this.cam.z);
    return Math.hypot(bx - hx, by - hy) * this.cam.z <= (this.coarse ? 22 : 10) ? id : null;
  }

  /** The selected image whose resize grip (bottom-right corner) is under the board point. */
  private imageGripAt(bx: number, by: number) {
    if (!this.selectedImage || this.readOnly) return null;
    const img = this.store.image(this.selectedImage);
    if (!img) return null;
    return Math.max(Math.abs(bx - (img.x + img.w)), Math.abs(by - (img.y + img.h))) * this.cam.z <= (this.coarse ? 22 : 8) ? img : null;
  }

  /** The single selected card whose resize grip (bottom-left corner) is under the board point. */
  private gripAt(bx: number, by: number): string | null {
    if (this.selection.size !== 1 || this.selectedLink || this.readOnly || this.cam.z < 0.3) return null;
    const id = [...this.selection][0];
    const c = this.store.card(id);
    if (!c) return null;
    const [gx, gy] = resizePoint(c);
    return Math.max(Math.abs(bx - gx), Math.abs(by - gy)) * this.cam.z <= (this.coarse ? 22 : 8) ? id : null;
  }

  /** Topmost drawing whose line passes within a few screen pixels of the board point. */
  private strokeAt(bx: number, by: number): string | null {
    const tol = (this.coarse ? 14 : 6) / this.cam.z;
    const list = this.store.strokes();
    for (let i = list.length - 1; i >= 0; i--) if (strokeHit(list[i], bx, by, tol)) return list[i].id;
    return null;
  }

  /** The eraser passed from one board point to the next: every stroke it touched goes (on release). */
  private eraseAlong(x0: number, y0: number, x1: number, y1: number) {
    const tol = (this.coarse ? 16 : 10) / this.cam.z;
    const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (tol / 2)));
    const list = this.store.strokes();
    for (let i = 0; i <= n; i++) {
      const x = x0 + ((x1 - x0) * i) / n, y = y0 + ((y1 - y0) * i) / n;
      for (const s of list) if (!this.inkErase.has(s.id) && strokeHit(s, x, y, tol)) this.inkErase.add(s.id);
    }
  }

  private openInkMenu(clientX: number, clientY: number) {
    const menu = $("#lw-menu");
    const ids = [...this.selectedStrokes];
    const del = el("button", "menu-action danger", ids.length > 1 ? `${ids.length} Zeichnungen löschen` : "Zeichnung löschen");
    del.type = "button";
    del.addEventListener("click", () => { this.store.deleteStrokes(ids); this.selectedStrokes.clear(); this.closeMenu(); this.requestRender(); });
    menu.replaceChildren(del);
    const sr = this.stage.getBoundingClientRect();
    menu.hidden = false;
    menu.style.transform = `translate(${Math.max(8, Math.min(clientX - sr.left, sr.width - menu.offsetWidth - 8))}px, ${Math.max(8, Math.min(clientY - sr.top, sr.height - menu.offsetHeight - 8))}px)`;
  }

  /** The drawing bar right of the rail: pen, marker, eraser, colours, sizes (kept per browser). */
  private showInkBar(on: boolean) {
    const bar = $("#lw-ink");
    if (!on) { bar.hidden = true; return; }
    const st = this.ink;
    const set = (f: () => void) => () => { f(); saveInk(st); this.showInkBar(true); };
    const tools = el("div", "ink-row");
    for (const [t, label, svg] of INK_TOOLS) {
      const b = el("button", "rail-btn");
      b.type = "button";
      b.title = label;
      b.setAttribute("aria-label", label);
      b.setAttribute("aria-pressed", String(st.tool === t));
      b.innerHTML = svg; // static icon markup
      b.addEventListener("click", set(() => { st.tool = t; }));
      tools.appendChild(b);
    }
    const colors = el("div", "ink-row");
    INK_COLORS.forEach((c, i) => {
      const b = el("button", "ink-color");
      b.type = "button";
      b.title = c.name;
      b.setAttribute("aria-label", `Farbe ${c.name}`);
      b.setAttribute("aria-pressed", String(st.color === i));
      b.style.background = css(c.rgb);
      b.addEventListener("click", set(() => { st.color = i; if (st.tool === "eraser") st.tool = "pen"; }));
      colors.appendChild(b);
    });
    const sizes = el("div", "ink-row");
    INK_SIZES.forEach((label, i) => {
      const b = el("button", "ink-size");
      b.type = "button";
      b.title = label;
      b.setAttribute("aria-label", `Strichstärke ${label}`);
      b.setAttribute("aria-pressed", String(st.size === i));
      const dot = el("span");
      dot.style.width = dot.style.height = `${4 + i * 4}px`;
      b.appendChild(dot);
      b.addEventListener("click", set(() => { st.size = i; if (st.tool === "eraser") st.tool = "pen"; }));
      sizes.appendChild(b);
    });
    bar.replaceChildren(tools, colors, sizes, el("div", "menu-hint", "Pencil oder Maus zeichnet. Hat ein Pencil gezeichnet, verschiebt der Finger das Board. Radierer: über Striche fahren."));
    bar.hidden = false;
    this.placeBesideRail(bar, $("#lw-tool-draw"));
  }

  /** Link whose line passes within a few screen pixels of the board point. */
  private linkAt(bx: number, by: number): string | null {
    let best: string | null = null, bestD = (this.coarse ? 14 : 6) / this.cam.z;
    for (const g of this.renderer.linkGeom) {
      const d = distance(bx, by, g.pts);
      if (d < bestD) { bestD = d; best = g.id; }
    }
    return best;
  }

  private bindInput() {
    const c = this.canvas;
    // ---- touch and pen (B7): two fingers zoom and move the view, a long press opens the menu of
    // what lies under it, a double tap does what a double click does ----
    const touches = new Map<number, { sx: number; sy: number }>();
    let pinch: { d: number; cx: number; cy: number; cam: Cam } | null = null;
    let press: { id: number; sx: number; sy: number; timer: number } | null = null;
    let down: { id: number; sx: number; sy: number; t: number } | null = null;
    let lastTap: { t: number; sx: number; sy: number } | null = null;
    let lastInk = { x: 0, y: 0 };
    /** All positions a fast pencil passed since the last event (where the browser keeps them). */
    const coalesced = (e: PointerEvent) => {
      const l = typeof e.getCoalescedEvents === "function" ? e.getCoalescedEvents() : [];
      return l.length ? l : [e];
    };
    const cancelPress = () => { if (press) window.clearTimeout(press.timer); press = null; };
    /** Drops whatever a pointer started, without a change (a second finger, a long press). */
    const abandon = () => {
      this.drag = null;
      this.frameDrag = null;
      this.imageDrag = null;
      this.resizing = null;
      this.imageResizing = null;
      this.linkDraft = null;
      this.inkDraft = null;
      this.inkDrag = null;
      this.inkErase = new Set();
      $("#lw-lasso").hidden = true;
      this.mode = { kind: "idle" };
      this.requestRender();
    };
    const mid = () => {
      const [p, q] = [...touches.values()];
      return { d: Math.max(1, Math.hypot(p.sx - q.sx, p.sy - q.sy)), cx: (p.sx + q.sx) / 2, cy: (p.sy + q.sy) / 2 };
    };
    // Safari's own page zoom would fight the board's pinch
    document.addEventListener("gesturestart", (e) => e.preventDefault());
    document.addEventListener("gesturechange", (e) => e.preventDefault());
    c.addEventListener("pointerdown", (e) => {
      this.lastPointerType = e.pointerType;
      this.coarse = e.pointerType !== "mouse";
      if (e.pointerType === "touch") {
        touches.set(e.pointerId, this.local(e));
        if (touches.size >= 2) {
          cancelPress();
          down = null;
          if (touches.size === 2) { abandon(); pinch = { ...mid(), cam: { ...this.cam } }; }
          return;
        }
      }
      if (e.button !== 0) return;
      this.closeMenu();
      this.commitEditor();
      try { c.setPointerCapture(e.pointerId); } catch { /* a pointer that is already gone */ }
      const { sx, sy } = this.local(e);
      down = { id: e.pointerId, sx, sy, t: performance.now() };
      const b = toBoard(this.cam, sx, sy);
      // drawing: pencil and mouse draw (fingers too, as long as no pencil has drawn), a finger
      // then only moves the view
      if (this.tool === "draw" && !this.readOnly) {
        if (e.pointerType === "pen") this.penSeen = true;
        if (e.pointerType !== "touch" || !this.penSeen) {
          this.selection.clear();
          this.selectedStrokes.clear();
          this.selectedLink = null;
          this.selectedFrame = null;
          this.selectedImage = null;
          lastInk = b;
          if (this.ink.tool === "eraser") {
            this.inkErase = new Set();
            this.eraseAlong(b.x, b.y, b.x, b.y);
            this.mode = { kind: "erase" };
          } else {
            this.inkDraft = { pts: [b.x, b.y, inkWidth(this.ink, e.pressure, e.pointerType, this.cam.z)], color: this.ink.color, kind: this.ink.tool === "marker" ? 1 : 0 };
            this.mode = { kind: "ink" };
          }
        } else {
          this.mode = { kind: "pan", sx, sy, cam: { ...this.cam }, moved: false };
        }
        this.requestRender();
        return;
      }
      const ig = this.imageGripAt(b.x, b.y);
      if (ig) {
        this.mode = { kind: "imageResize", id: ig.id, x: ig.x, y: ig.y, ratio: ig.h / ig.w };
        this.imageResizing = { id: ig.id, w: ig.w, h: ig.h };
        this.requestRender();
        return;
      }
      const grip = this.gripAt(b.x, b.y);
      const gc = grip ? this.store.card(grip) : null;
      if (gc) {
        this.mode = { kind: "resize", id: gc.id, right: gc.x + gc.w, top: gc.y };
        this.resizing = { id: gc.id, w: gc.w, h: gc.h };
        this.requestRender();
        return;
      }
      const from = this.readOnly ? null : this.handleAt(b.x, b.y);
      if (from) {
        this.mode = { kind: "link", from };
        this.linkDraft = { from, x: b.x, y: b.y, target: null };
        this.requestRender();
        return;
      }
      // drawings lie on top: a press on a line picks the stroke (Shift adds or removes it)
      const stroke = this.readOnly || this.tool === "lasso" ? null : this.strokeAt(b.x, b.y);
      if (stroke) {
        if (e.shiftKey) { if (this.selectedStrokes.has(stroke)) this.selectedStrokes.delete(stroke); else this.selectedStrokes.add(stroke); }
        else if (!this.selectedStrokes.has(stroke)) this.selectedStrokes = new Set([stroke]);
        this.selection.clear();
        this.selectedLink = null;
        this.selectedFrame = null;
        this.selectedImage = null;
        this.mode = { kind: "inkDrag", bx: b.x, by: b.y, moved: false };
        if (e.pointerType !== "mouse") {
          const { clientX, clientY } = e;
          press = { id: e.pointerId, sx, sy, timer: window.setTimeout(() => { press = null; down = null; abandon(); contextAt(clientX, clientY); }, 500) };
        }
        this.requestRender();
        return;
      }
      const hit = this.store.hitTest(b.x, b.y);
      const link = hit ? null : this.linkAt(b.x, b.y);
      // images lie above the frames: an image over a frame header is picked first
      const image = hit || link ? null : this.store.imageAt(b.x, b.y);
      const frame = hit || link || image ? null : this.frameHeaderAt(b.x, b.y);
      if (!e.shiftKey) this.selectedStrokes.clear();
      this.selectedLink = link;
      this.selectedFrame = frame;
      this.selectedImage = image;
      if (image) {
        this.selection.clear();
        this.mode = this.readOnly ? { kind: "idle" } : { kind: "image", id: image, bx: b.x, by: b.y, moved: false };
      } else if (link) {
        this.selection.clear();
        this.mode = { kind: "idle" };
      } else if (frame) {
        this.selection.clear();
        this.mode = this.readOnly ? { kind: "idle" } : { kind: "frame", id: frame, bx: b.x, by: b.y, moved: false };
      } else if (hit) {
        if (e.shiftKey) {
          if (this.selection.has(hit)) this.selection.delete(hit);
          else this.selection.add(hit);
        } else if (!this.selection.has(hit)) this.selection = new Set([hit]);
        this.mode = this.readOnly ? { kind: "idle" } : { kind: "drag", bx: b.x, by: b.y, moved: false };
      } else if (e.shiftKey || this.tool === "lasso") {
        // the lasso tool starts a new selection, Shift adds to the current one
        if (!e.shiftKey) { this.selection.clear(); this.selectedStrokes.clear(); }
        this.mode = { kind: "lasso", bx: b.x, by: b.y, x: b.x, y: b.y };
      } else {
        this.mode = { kind: "pan", sx, sy, cam: { ...this.cam }, moved: false };
      }
      // touch and pen have no right click: holding still on something for half a second opens its menu
      if (e.pointerType !== "mouse" && (hit || link || image || frame)) {
        const { clientX, clientY } = e;
        press = { id: e.pointerId, sx, sy, timer: window.setTimeout(() => { press = null; down = null; abandon(); contextAt(clientX, clientY); }, 500) };
      }
      this.requestRender();
    });
    c.addEventListener("pointermove", (e) => {
      if (e.pointerType === "touch" && touches.has(e.pointerId)) {
        touches.set(e.pointerId, this.local(e));
        if (pinch) {
          // the board point that lay under the fingers' midpoint stays under their midpoint
          if (touches.size >= 2) {
            const m = mid(), p = pinch;
            const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, p.cam.z * m.d / p.d));
            const at = toBoard(p.cam, p.cx, p.cy);
            this.setCam({ x: at.x - m.cx / z, y: at.y - m.cy / z, z });
          }
          return;
        }
      }
      if (press && press.id === e.pointerId) {
        const p = this.local(e);
        if (Math.hypot(p.sx - press.sx, p.sy - press.sy) > 8) cancelPress();
      }
      const { sx, sy } = this.local(e);
      this.pointer = toBoard(this.cam, sx, sy);
      const m = this.mode;
      if (m.kind === "idle") {
        const b = toBoard(this.cam, sx, sy);
        const onCard = this.store.hitTest(b.x, b.y) !== null;
        const cursor = this.readOnly ? "default"
          : this.tool === "draw" ? "crosshair"
          : this.handleAt(b.x, b.y) ? "crosshair"
          : this.gripAt(b.x, b.y) ? "nesw-resize"
          : this.imageGripAt(b.x, b.y) ? "nwse-resize"
          : !onCard && this.linkAt(b.x, b.y) ? "pointer"
          : !onCard && this.frameHeaderAt(b.x, b.y) ? "move"
          : !onCard && this.tool === "lasso" ? "crosshair" : "default";
        if (c.style.cursor !== cursor) c.style.cursor = cursor;
      } else if (m.kind === "frame") {
        const b = toBoard(this.cam, sx, sy);
        const dx = b.x - m.bx, dy = b.y - m.by;
        if ((Math.abs(dx) + Math.abs(dy)) * this.cam.z > 3) m.moved = true;
        const rect = this.frameRect(m.id);
        if (m.moved && rect) {
          this.frameDrag = { id: m.id, dx, dy, rect: this.frameDrag?.rect ?? rect };
          this.requestRender();
        }
      } else if (m.kind === "image") {
        const b = toBoard(this.cam, sx, sy);
        const dx = b.x - m.bx, dy = b.y - m.by;
        if ((Math.abs(dx) + Math.abs(dy)) * this.cam.z > 3) m.moved = true;
        if (m.moved) { this.imageDrag = { ids: new Set([m.id]), dx, dy }; this.requestRender(); }
      } else if (m.kind === "imageResize") {
        // the top-left corner and the aspect ratio stay
        const b = toBoard(this.cam, sx, sy);
        const w = Math.min(8000, Math.max(20, Math.round(b.x - m.x)));
        this.imageResizing = { id: m.id, w, h: Math.max(20, Math.round(w * m.ratio)) };
        this.requestRender();
      } else if (m.kind === "resize") {
        // the top-right corner stays; sizes in steps of 10 board px
        const b = toBoard(this.cam, sx, sy);
        const step = (v: number) => Math.min(800, Math.max(50, Math.round(v / 10) * 10));
        this.resizing = { id: m.id, w: step(m.right - b.x), h: step(b.y - m.top) };
        this.requestRender();
      } else if (m.kind === "link") {
        const b = toBoard(this.cam, sx, sy);
        const t = this.store.hitTest(b.x, b.y);
        this.linkDraft = { from: m.from, x: b.x, y: b.y, target: t && t !== m.from ? t : null };
        this.requestRender();
      } else if (m.kind === "pan") {
        const dx = sx - m.sx, dy = sy - m.sy;
        if (Math.abs(dx) + Math.abs(dy) > 2) m.moved = true;
        this.setCam({ x: m.cam.x - dx / this.cam.z, y: m.cam.y - dy / this.cam.z, z: this.cam.z });
      } else if (m.kind === "drag") {
        const b = toBoard(this.cam, sx, sy);
        const dx = b.x - m.bx, dy = b.y - m.by;
        if (Math.abs(dx) * this.cam.z + Math.abs(dy) * this.cam.z > 3) m.moved = true;
        if (m.moved) {
          this.drag = { ids: new Set(this.selection), dx, dy };
          this.requestRender();
        }
      } else if (m.kind === "ink" && this.inkDraft) {
        const d = this.inkDraft, step = 1.5 / this.cam.z;
        for (const ev of coalesced(e)) {
          const p = this.local(ev), b = toBoard(this.cam, p.sx, p.sy), n = d.pts.length;
          if (n / 3 >= MAX_INK_POINTS || Math.hypot(b.x - d.pts[n - 3], b.y - d.pts[n - 2]) < step) continue;
          d.pts.push(b.x, b.y, inkWidth(this.ink, ev.pressure, ev.pointerType, this.cam.z));
        }
        this.requestRender();
      } else if (m.kind === "erase") {
        for (const ev of coalesced(e)) {
          const p = this.local(ev), b = toBoard(this.cam, p.sx, p.sy);
          this.eraseAlong(lastInk.x, lastInk.y, b.x, b.y);
          lastInk = b;
        }
        this.requestRender();
      } else if (m.kind === "inkDrag") {
        const b = toBoard(this.cam, sx, sy);
        const dx = b.x - m.bx, dy = b.y - m.by;
        if ((Math.abs(dx) + Math.abs(dy)) * this.cam.z > 3) m.moved = true;
        if (m.moved) { this.inkDrag = { dx, dy }; this.requestRender(); }
      } else if (m.kind === "lasso") {
        const b = toBoard(this.cam, sx, sy);
        m.x = b.x;
        m.y = b.y;
        this.showLasso(m);
      }
    });
    const end = (e: PointerEvent) => {
      cancelPress();
      if (e.pointerType === "touch") {
        touches.delete(e.pointerId);
        if (pinch || touches.size) { if (touches.size < 2) pinch = null; return; }
      }
      const d0 = down, up = this.local(e);
      down = null;
      const tapped = !!d0 && d0.id === e.pointerId && e.type === "pointerup" && e.pointerType !== "mouse"
        && performance.now() - d0.t < 350 && Math.hypot(up.sx - d0.sx, up.sy - d0.sy) < 10;
      const m = this.mode;
      if (m.kind === "frame") {
        const d = this.frameDrag;
        this.frameDrag = null;
        if (m.moved && d) this.store.moveFrame(m.id, Math.round(d.rect[0] + d.dx), Math.round(d.rect[1] + d.dy));
      } else if (m.kind === "image") {
        const d = this.imageDrag;
        this.imageDrag = null;
        if (m.moved && d) this.store.moveImages([m.id], Math.round(d.dx), Math.round(d.dy));
      } else if (m.kind === "imageResize") {
        const r = this.imageResizing;
        this.imageResizing = null;
        const img = this.store.image(m.id);
        if (r && img && (r.w !== img.w || r.h !== img.h)) this.store.resizeImage(m.id, r.w, r.h);
      } else if (m.kind === "resize") {
        const r = this.resizing;
        this.resizing = null;
        const c = this.store.card(m.id);
        if (r && c && (r.w !== c.w || r.h !== c.h)) this.store.resizeCard(m.id, r.w, r.h);
      } else if (m.kind === "link") {
        const target = this.linkDraft?.target;
        this.linkDraft = null;
        // select the successor, so the next dependency can be dragged straight from it
        if (target && this.store.createLink(m.from, target)) this.selection = new Set([target]);
      } else if (m.kind === "drag" && m.moved && this.drag) {
        const { dx, dy } = this.drag;
        this.drag = null;
        this.store.moveCards([...this.selection], dx, dy);
      } else if (m.kind === "pan" && !m.moved) {
        this.selection.clear();
        this.selectedStrokes.clear();
        this.selectedFrame = null;
        this.selectedImage = null;
      } else if (m.kind === "ink") {
        const d = this.inkDraft;
        this.inkDraft = null;
        if (d) this.store.addStroke(d.pts, d.color, d.kind);
      } else if (m.kind === "erase") {
        const ids = [...this.inkErase];
        this.inkErase = new Set();
        this.store.deleteStrokes(ids);
      } else if (m.kind === "inkDrag") {
        const d = this.inkDrag;
        this.inkDrag = null;
        if (m.moved && d) this.store.moveStrokes([...this.selectedStrokes], d.dx, d.dy);
      } else if (m.kind === "lasso") {
        const x = Math.min(m.bx, m.x), y = Math.min(m.by, m.y);
        for (const id of this.store.queryIds(x, y, Math.abs(m.x - m.bx), Math.abs(m.y - m.by))) this.selection.add(id);
        // drawings wholly inside the lasso too
        const x2 = x + Math.abs(m.x - m.bx), y2 = y + Math.abs(m.y - m.by);
        for (const s of this.store.strokes()) if (s.x >= x && s.y >= y && s.x + s.w <= x2 && s.y + s.h <= y2) this.selectedStrokes.add(s.id);
        $("#lw-lasso").hidden = true;
      }
      this.drag = null;
      this.mode = { kind: "idle" };
      this.requestRender();
      // two taps close together: a double tap (touch and pen; the browser's dblclick is for the mouse)
      if (tapped && this.tool !== "draw") {
        const now = performance.now();
        if (lastTap && now - lastTap.t < 400 && Math.hypot(up.sx - lastTap.sx, up.sy - lastTap.sy) < 30) { lastTap = null; doubleAt(up.sx, up.sy); }
        else lastTap = { t: now, sx: up.sx, sy: up.sy };
      }
    };
    c.addEventListener("pointerup", end);
    c.addEventListener("pointercancel", end);
    const doubleAt = (sx: number, sy: number) => {
      const b = toBoard(this.cam, sx, sy);
      const hit = this.store.hitTest(b.x, b.y);
      // the card header (owner strip) opens the details; readers only get the details
      const card = hit ? this.store.card(hit) : null;
      if (card && (this.readOnly || b.y - card.y < 18)) return this.openDetails(card.id);
      if (this.readOnly) return;
      const frame = hit ? null : this.frameHeaderAt(b.x, b.y);
      if (frame) this.editFrame(frame);
      else if (hit) this.openEditor(hit);
      else {
        const id = this.store.createCard({ x: b.x - 50, y: b.y - 50 });
        this.selection = new Set([id]);
        this.openEditor(id);
      }
    };
    c.addEventListener("dblclick", (e) => {
      if (this.lastPointerType !== "mouse") return; // touch and pen: the double tap in end()
      const { sx, sy } = this.local(e);
      doubleAt(sx, sy);
    });
    c.addEventListener("dragover", (e) => {
      if (this.readOnly || ![...(e.dataTransfer?.items ?? [])].some((i) => i.kind === "file")) return;
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
    });
    c.addEventListener("drop", (e) => {
      const files = [...(e.dataTransfer?.files ?? [])].filter(isImage);
      if (this.readOnly || !files.length) return;
      e.preventDefault();
      const { sx, sy } = this.local(e);
      void this.addPictures(files, toBoard(this.cam, sx, sy));
    });
    /** The menu of what lies under a screen point: right click, or a long press on touch and pen. */
    const contextAt = (clientX: number, clientY: number) => {
      if (this.readOnly) return this.closeMenu();
      const { sx, sy } = this.local({ clientX, clientY });
      const sb = toBoard(this.cam, sx, sy);
      const stroke = this.strokeAt(sb.x, sb.y);
      if (stroke) {
        if (!this.selectedStrokes.has(stroke)) this.selectedStrokes = new Set([stroke]);
        this.selection.clear();
        this.requestRender();
        return this.openInkMenu(clientX, clientY);
      }
      const b = toBoard(this.cam, sx, sy);
      const hit = this.store.hitTest(b.x, b.y);
      if (!hit) {
        const link = this.linkAt(b.x, b.y);
        const image = link ? null : this.store.imageAt(b.x, b.y);
        if (image) {
          this.selectedImage = image;
          this.selection.clear();
          this.selectedLink = null;
          this.requestRender();
          return this.openImageMenu(image, clientX, clientY);
        }
        const frame = link ? null : this.frameHeaderAt(b.x, b.y);
        if (frame) {
          this.selectedFrame = frame;
          this.selection.clear();
          this.requestRender();
          return this.openFrameMenu(frame, clientX, clientY);
        }
        if (!link) return this.closeMenu();
        this.selectedLink = link;
        this.selection.clear();
        this.requestRender();
        return this.openLinkMenu(link, clientX, clientY);
      }
      this.selectedLink = null;
      if (!this.selection.has(hit)) this.selection = new Set([hit]);
      this.requestRender();
      this.openMenu(hit, clientX, clientY);
    };
    c.addEventListener("contextmenu", (e) => { e.preventDefault(); contextAt(e.clientX, e.clientY); });
    c.addEventListener("wheel", (e) => {
      e.preventDefault();
      const { sx, sy } = this.local(e);
      const trackpadPan = !e.ctrlKey && e.deltaMode === 0 && (Math.abs(e.deltaX) > 0 || Math.abs(e.deltaY) < 40);
      if (trackpadPan) this.setCam({ ...this.cam, x: this.cam.x + e.deltaX / this.cam.z, y: this.cam.y + e.deltaY / this.cam.z });
      else this.setCam(zoomAt(this.cam, sx, sy, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015) * (e.deltaMode === 1 ? 33 : 1))));
    }, { passive: false });
    window.addEventListener("keydown", (e) => {
      const typing = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || (e.target instanceof HTMLElement && e.target.isContentEditable);
      if (typing) return;
      const mod = e.metaKey || e.ctrlKey;
      if ((e.key === "/" && !mod) || (mod && e.key.toLowerCase() === "f")) {
        // the board is a canvas, so the browser's own find cannot see the cards
        e.preventDefault();
        this.filters.focusSearch();
      } else if (e.key.toLowerCase() === "m" && !mod) {
        this.toggleMap();
      } else if (["v", "l", "p"].includes(e.key.toLowerCase()) && !mod && !e.altKey && !this.readOnly) {
        const k = e.key.toLowerCase();
        this.setTool(k === "l" ? "lasso" : k === "p" ? "draw" : "select");
      } else if (e.key.toLowerCase() === "i" && !mod && this.selection.size === 1) {
        e.preventDefault();
        this.openDetails([...this.selection][0]);
      } else if (e.key === "Escape") {
        this.selection.clear();
        this.selectedLink = null;
        this.selectedFrame = null;
        this.selectedImage = null;
        if (this.mode.kind === "link") { this.mode = { kind: "idle" }; this.linkDraft = null; }
        this.closeMenu();
        this.closeFan();
        this.selectedStrokes.clear();
        this.requestRender();
      } else if (this.readOnly) {
        // readers: search, selection and Escape only
      } else if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (e.shiftKey) this.store.redo();
        else this.store.undo();
      } else if (mod && e.key.toLowerCase() === "d" && this.selection.size) {
        e.preventDefault(); // not the browser's bookmark dialog
        this.duplicate();
      } else if (mod && e.key.toLowerCase() === "y") {
        e.preventDefault();
        this.store.redo();
      } else if ((e.key === "Delete" || e.key === "Backspace") && this.selectedStrokes.size && !this.selection.size) {
        e.preventDefault();
        this.store.deleteStrokes([...this.selectedStrokes]);
        this.selectedStrokes.clear();
      } else if ((e.key === "Delete" || e.key === "Backspace") && this.selectedFrame) {
        e.preventDefault();
        this.store.deleteFrame(this.selectedFrame);
        this.selectedFrame = null;
      } else if (e.key === "Enter" && this.selectedFrame) {
        e.preventDefault();
        this.editFrame(this.selectedFrame);
      } else if ((e.key === "Delete" || e.key === "Backspace") && this.selectedImage) {
        e.preventDefault();
        this.store.deleteImages([this.selectedImage]);
        this.selectedImage = null;
      } else if ((e.key === "Delete" || e.key === "Backspace") && this.selectedLink) {
        e.preventDefault();
        this.store.deleteLinks([this.selectedLink]);
        this.selectedLink = null;
      } else if ((e.key === "Delete" || e.key === "Backspace") && this.selection.size) {
        e.preventDefault();
        this.store.deleteCards([...this.selection]);
        this.selection.clear();
      } else if (e.key === "Enter" && this.selection.size === 1) {
        e.preventDefault();
        this.openEditor([...this.selection][0]);
      }
    });
  }

  // ---------- messages, Excel export ----------

  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  /** Short message above the dock. */
  toast(msg: string, error = false) {
    const t = $("#lw-toast");
    t.textContent = msg;
    t.classList.toggle("error", error);
    t.hidden = false;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { t.hidden = true; }, error ? 6000 : 3000);
  }

  /** Downloads the board (or the given cards) as an Excel file, made by the server. */
  async exportExcel(cards?: string[]) {
    const url = `./api/boards/${encodeURIComponent(this.boardId)}/export.xlsx`;
    try {
      const r = cards
        ? await fetch(url, { method: "POST", headers: { "content-type": "application/json", "x-lanewerk": "1" }, body: JSON.stringify({ cards }) })
        : await fetch(url);
      if (r.status === 404) return this.toast("Das Board ist noch leer – nichts zu exportieren.", true);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") ?? "";
      const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      const plain = /filename="([^"]+)"/i.exec(cd);
      const name = star ? decodeURIComponent(star[1]) : plain ? plain[1] : "lanewerk.xlsx";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      this.toast(`${name} heruntergeladen`);
    } catch (e) {
      this.toast(`Excel-Export fehlgeschlagen (${(e as Error).message}).`, true);
    }
  }

  // ---------- clipboard: copy, cut, paste, duplicate ----------

  private bindClipboard() {
    this.canvas.addEventListener("pointerleave", () => { this.pointer = null; });
    // text fields keep the browser's own copy and paste
    const typing = (t: EventTarget | null) => t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || (t instanceof HTMLElement && t.isContentEditable);
    const copy = (e: ClipboardEvent, cut: boolean) => {
      if (typing(e.target) || !this.selection.size || !e.clipboardData) return;
      // text selected in the details panel (a read-only description, the history) is copied as
      // text – unless the board has the keyboard
      const details = document.getElementById("lw-details");
      if (details && e.target instanceof Node && details.contains(e.target) && document.activeElement !== this.canvas) return;
      if (!this.stage.isConnected || this.stage.offsetParent === null) return;
      e.preventDefault();
      const ids = [...this.selection];
      // the board it comes from: pictures in descriptions are copied along when pasted elsewhere
      let clip = this.store.copyCards(ids);
      try { clip = JSON.stringify({ ...JSON.parse(clip), from: this.boardId }); } catch { /* keep as is */ }
      e.clipboardData.setData(CLIP_TYPE, clip);
      // plain text for other programs: one title per line
      e.clipboardData.setData("text/plain", ids.map((id) => this.store.card(id)?.title ?? "").join("\n"));
      if (cut && !this.readOnly) {
        this.store.deleteCards(ids);
        this.selection.clear();
      }
    };
    document.addEventListener("copy", (e) => copy(e, false));
    document.addEventListener("cut", (e) => copy(e, true));
    document.addEventListener("paste", (e) => {
      if (typing(e.target) || this.readOnly || !e.clipboardData || this.stage.offsetParent === null) return;
      // a picture (screenshot, copied image) becomes an image on the board
      const pictures = [...e.clipboardData.files].filter(isImage);
      if (pictures.length) {
        e.preventDefault();
        void this.addPictures(pictures, this.pointer);
        return;
      }
      const clip = e.clipboardData.getData(CLIP_TYPE);
      const text = e.clipboardData.getData("text/plain");
      if (!clip && !text.trim()) return;
      e.preventDefault();
      this.paste(clip || this.clipFromText(text));
    });
  }

  /** Uploads pictures and puts them on the board, centred at `at` (else the middle of the view), side by side. */
  async addPictures(files: Blob[], at: { x: number; y: number } | null) {
    if (this.readOnly) return;
    const r = this.stage.getBoundingClientRect();
    let p = at ?? toBoard(this.cam, r.width / 2, r.height / 2);
    this.toast(files.length === 1 ? "Bild wird hochgeladen …" : `${files.length} Bilder werden hochgeladen …`);
    for (const f of files.slice(0, 20)) {
      try {
        const { blob, w, h } = await prepare(f);
        const file = await upload(this.boardId, blob);
        const size = placeSize(w, h);
        const id = this.store.addImage(file, Math.round(p.x - size.w / 2), Math.round(p.y - size.h / 2), size.w, size.h);
        if (id) { this.selectedImage = id; this.selection.clear(); }
        p = { x: p.x + size.w + 20, y: p.y };
      } catch (e) {
        this.toast(`Bild nicht übernommen (${(e as Error).message}).`, true);
        return;
      }
    }
    $("#lw-toast").hidden = true;
    this.requestRender();
  }

  /** Right click on an image: order and delete. */
  private openImageMenu(id: string, clientX: number, clientY: number) {
    const menu = $("#lw-menu");
    menu.replaceChildren();
    const item = (label: string, fn: () => void, cls = "") => {
      const b = el("button", `menu-action ${cls}`, label);
      b.type = "button";
      b.addEventListener("click", () => { fn(); this.closeMenu(); });
      menu.appendChild(b);
    };
    item("Nach vorne", () => this.store.imageTo(id, true));
    item("Nach hinten", () => this.store.imageTo(id, false));
    item("Bild löschen", () => { this.store.deleteImages([id]); this.selectedImage = null; }, "danger");
    this.placeMenu(menu, clientX, clientY);
  }

  /** Lines of plain text (a list from Excel, Teams, …) become cards, eight per row. */
  private clipFromText(text: string): string {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).slice(0, 200);
    return JSON.stringify({
      cards: lines.map((t, i) => ({ x: (i % 8) * 110, y: Math.floor(i / 8) * 110, w: 100, h: 100, title: t.slice(0, 500) })),
      links: [],
    });
  }

  /** Pastes a clip with its top-left corner at the pointer (or the middle of the view). */
  private paste(json: string) {
    let clip: { cards: { x: number; y: number; w: number; h: number; rich?: { insert?: unknown }[] }[]; from?: unknown };
    try { clip = JSON.parse(json); } catch { return; }
    if (!Array.isArray(clip?.cards) || !clip.cards.length) return;
    const x0 = Math.min(...clip.cards.map((c) => Number(c.x) || 0));
    const y0 = Math.min(...clip.cards.map((c) => Number(c.y) || 0));
    let at = this.pointer;
    if (!at) {
      const r = this.stage.getBoundingClientRect();
      at = toBoard(this.cam, r.width / 2 - 50 * this.cam.z, r.height / 2 - 50 * this.cam.z);
    }
    const ids = this.store.pasteCards(json, Math.round(at.x - x0), Math.round(at.y - y0));
    if (ids.length && typeof clip.from === "string" && clip.from !== this.boardId) {
      const files = new Set<string>();
      for (const c of clip.cards) for (const op of Array.isArray(c.rich) ? c.rich : []) {
        const f = (op?.insert as { image?: unknown } | undefined)?.image;
        if (typeof f === "string" && /^[0-9a-f]{64}\.(png|jpg|webp|gif)$/.test(f)) files.add(f);
      }
      if (files.size) void this.copyFiles(clip.from, [...files].slice(0, 50));
    }
    if (ids.length) {
      this.selection = new Set(ids);
      this.selectedLink = null;
      this.selectedFrame = null;
      this.requestRender();
    }
  }

  /** Pictures of pasted descriptions: from the board they were copied on to this one (same name, the
   *  content decides it). Fails quietly per picture when that board is not readable here. */
  private async copyFiles(from: string, files: string[]) {
    let failed = 0;
    for (const f of files) {
      try {
        const r = await fetch(fileUrl(from, f), { credentials: "same-origin" });
        if (!r.ok) throw new Error(String(r.status));
        if ((await upload(this.boardId, await r.blob())) !== f) failed++;
      } catch { failed++; }
    }
    if (failed) this.toast(`${failed === 1 ? "Ein Bild" : `${failed} Bilder`} aus Beschreibungen nicht übernommen.`, true);
  }

  private duplicate() {
    if (this.readOnly || !this.selection.size) return;
    const ids = this.store.duplicateCards([...this.selection], DUP_DX, DUP_DY);
    if (ids.length) { this.selection = new Set(ids); this.requestRender(); }
  }

  /** Copy / cut from the menu: runs the same clipboard handler as ⌘C (then deletes for cut). */
  private copyToClipboard(cut: boolean) {
    this.focus();
    document.execCommand("copy");
    if (cut && !this.readOnly) {
      this.store.deleteCards([...this.selection]);
      this.selection.clear();
    }
  }

  private async copyCardLink(id: string) {
    const url = `${location.origin}${location.pathname}?board=${encodeURIComponent(this.boardId)}&card=${encodeURIComponent(id)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // no async clipboard (plain http on another host): copy through a hidden field
      const t = document.createElement("textarea");
      t.value = url;
      t.style.cssText = "position:fixed;left:-9999px";
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      t.remove();
      this.focus();
    }
  }

  private showLasso(m: { bx: number; by: number; x: number; y: number }) {
    const a = toScreen(this.cam, Math.min(m.bx, m.x), Math.min(m.by, m.y));
    const box = $("#lw-lasso");
    box.hidden = false;
    box.style.transform = `translate(${a.x}px, ${a.y}px)`;
    box.style.width = `${Math.abs(m.x - m.bx) * this.cam.z}px`;
    box.style.height = `${Math.abs(m.y - m.by) * this.cam.z}px`;
  }

  // ---------- title editor ----------

  openEditor(id: string) {
    const card = this.store.card(id);
    if (!card || this.readOnly) return;
    this.closeMenu();
    this.editing = id;
    const ed = $<HTMLTextAreaElement>("#lw-editor");
    ed.value = card.title;
    ed.hidden = false;
    this.positionEditor();
    ed.focus();
    ed.select();
    this.requestRender();
  }

  private positionEditor() {
    if (!this.editing) return;
    const card = this.store.card(this.editing);
    const ed = $<HTMLTextAreaElement>("#lw-editor");
    if (!card) { this.editing = null; ed.hidden = true; return; }
    const p = toScreen(this.cam, card.x, card.y + 18);
    ed.style.width = `${card.w}px`;
    ed.style.height = `${card.h - 18}px`;
    ed.style.transform = `translate(${p.x}px, ${p.y}px) scale(${this.cam.z})`;
  }

  commitEditor(save = true) {
    if (!this.editing) return;
    const id = this.editing;
    const ed = $<HTMLTextAreaElement>("#lw-editor");
    this.editing = null;
    ed.hidden = true;
    // give the keyboard back to the board (undo, delete, … only act when no text field has focus)
    this.canvas.focus({ preventScroll: true });
    const text = ed.value.replace(/\s+/g, " ").trim();
    if (save && this.store.card(id)?.title !== text) this.store.setTitle(id, text);
    this.requestRender();
  }

  bindEditor() {
    const ed = $<HTMLTextAreaElement>("#lw-editor");
    ed.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.commitEditor(); }
      else if (e.key === "Escape") { e.preventDefault(); this.commitEditor(false); }
      e.stopPropagation();
    });
    ed.addEventListener("blur", () => this.commitEditor());
  }

  // ---------- context menu ----------

  private closeMenu() {
    const m = document.getElementById("lw-menu");
    if (m) m.hidden = true;
  }

  private openMenu(id: string, clientX: number, clientY: number) {
    const menu = $("#lw-menu");
    menu.replaceChildren();
    const ids = [...this.selection];
    const card = this.store.card(id)!;
    const section = (title: string) => {
      const s = el("div", "menu-section");
      s.appendChild(el("div", "menu-title", title));
      menu.appendChild(s);
      return s;
    };
    if (ids.length === 1) {
      const details = el("button", "menu-action", "Details … (I)");
      details.type = "button";
      details.title = "Beschreibung, Link und alle Angaben der Karte";
      details.addEventListener("click", () => this.openDetails(id));
      menu.appendChild(details);
    }
    const edit = el("div", "menu-row menu-edit");
    const action = (label: string, title: string, fn: () => void) => {
      const b = el("button", "chip plain", label);
      b.type = "button";
      b.title = title;
      b.addEventListener("click", () => { fn(); this.closeMenu(); });
      edit.appendChild(b);
    };
    action("Duplizieren", "Kopie daneben (⌘D)", () => this.duplicate());
    action("Kopieren", "In die Zwischenablage, auch für andere Boards (⌘C)", () => this.copyToClipboard(false));
    action("Ausschneiden", "Kopieren und entfernen (⌘X)", () => this.copyToClipboard(true));
    if (ids.length === 1) action("Link kopieren", "Adresse, die dieses Board auf der Karte öffnet", () => void this.copyCardLink(id));
    action("Excel", ids.length > 1 ? `Diese ${ids.length} Karten als Excel-Datei` : "Diese Karte als Excel-Datei", () => void this.exportExcel(ids));
    menu.appendChild(edit);
    const status = section(ids.length > 1 ? `Status (${ids.length} Karten)` : "Status");
    const row = el("div", "menu-row");
    ([1, 2, 4, 3] as Status[]).forEach((s) => {
      const b = el("button", "chip", STATUS[s].label);
      b.type = "button";
      b.style.setProperty("--dot", css(STATUS[s].rgb));
      if (card.status === s) b.setAttribute("aria-pressed", "true");
      b.addEventListener("click", () => { this.store.setStatus(ids, s); this.closeMenu(); });
      row.appendChild(b);
    });
    status.appendChild(row);
    const colors = section("Farbe");
    const grid = el("div", "swatches");
    STICKY.forEach((c, i) => {
      const b = el("button", "swatch");
      b.type = "button";
      b.title = c.name;
      b.setAttribute("aria-label", c.name);
      b.style.background = css(c.rgb);
      if (card.color === i) b.setAttribute("aria-pressed", "true");
      b.addEventListener("click", () => { this.store.setColor(ids, i); this.closeMenu(); });
      grid.appendChild(b);
    });
    colors.appendChild(grid);
    const who = section("Verantwortlich (Kürzel)");
    const owner = el("input", "field");
    owner.id = "lw-menu-owner";
    owner.maxLength = 4;
    owner.placeholder = "z. B. JUEN";
    owner.value = card.owner ?? "";
    owner.setAttribute("aria-label", "Kürzel der verantwortlichen Person");
    owner.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Enter") { this.store.setOwner(ids, owner.value.trim().toUpperCase()); this.closeMenu(); }
    });
    who.appendChild(owner);
    const due = section(ids.length > 1 ? `Fällig am (${ids.length} Karten)` : "Fällig am");
    const date = el("input", "field");
    date.id = "lw-menu-due";
    date.type = "date";
    date.value = card.due ?? "";
    date.setAttribute("aria-label", "Fälligkeitsdatum");
    date.addEventListener("change", () => this.store.setDue(ids, date.value));
    date.addEventListener("keydown", (e) => e.stopPropagation());
    due.appendChild(date);
    const align = el("button", "menu-action", "An Fälligkeit ausrichten");
    align.type = "button";
    align.addEventListener("click", () => {
      if (!this.store.align(id)) align.textContent = "Keine passende Kalenderwoche im Rahmen";
      else this.closeMenu();
    });
    // aligning moves one card into its week – only offered for a single card
    if (ids.length === 1) due.appendChild(align);
    if (card.week) due.appendChild(el("div", "menu-hint", `Liegt in ${card.week.replace("-W", " · KW ")}`));
    const sp = section(ids.length > 1 ? `Story Points (${ids.length} Karten)` : "Story Points");
    const points = el("input", "field");
    points.id = "lw-menu-points";
    points.inputMode = "decimal";
    points.maxLength = 6;
    points.placeholder = "z. B. 3 – leer entfernt";
    points.value = card.points == null ? "" : formatPoints(card.points);
    points.setAttribute("aria-label", "Story Points");
    points.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key !== "Enter") return;
      if (this.store.setPoints(ids, points.value)) this.closeMenu();
      else { points.setAttribute("aria-invalid", "true"); points.title = "Eine Zahl von 0 bis 999"; }
    });
    sp.appendChild(points);
    if (ids.length === 2) {
      // on a timeline the left card comes first: it becomes the predecessor
      const [a, b] = ids.map((i) => this.store.card(i)!).sort((p, q) => p.x + p.w - (q.x + q.w) || p.y - q.y).map((c) => c.id);
      const short = (id: string) => {
        const t = this.store.card(id)?.title || "Karte";
        return t.length > 16 ? `${t.slice(0, 15)}…` : t;
      };
      const link = el("button", "menu-action", `Abhängigkeit: ${short(a)} → ${short(b)}`);
      link.type = "button";
      link.addEventListener("click", () => {
        if (this.store.createLink(a, b)) this.closeMenu();
        else link.textContent = "Die beiden Karten sind schon verbunden";
      });
      menu.appendChild(link);
    }
    const del = el("button", "menu-action danger", ids.length > 1 ? `${ids.length} Karten löschen` : "Karte löschen");
    del.type = "button";
    del.addEventListener("click", () => { this.store.deleteCards(ids); this.selection.clear(); this.closeMenu(); });
    menu.appendChild(del);
    this.placeMenu(menu, clientX, clientY);
  }

  // ---------- link menu ----------

  private openLinkMenu(id: string, clientX: number, clientY: number) {
    const l = this.store.link(id);
    if (!l) return;
    const menu = $("#lw-menu");
    menu.replaceChildren();
    const section = (title: string) => {
      const s = el("div", "menu-section");
      s.appendChild(el("div", "menu-title", title));
      menu.appendChild(s);
      return s;
    };
    const kind = section("Verbindung");
    const row = el("div", "menu-row");
    ([[1, "Abhängigkeit"], [2, "Linie"]] as [LinkKind, string][]).forEach(([k, label]) => {
      const b = el("button", "chip link-kind", label);
      b.type = "button";
      b.setAttribute("aria-pressed", String(l.kind === k));
      b.addEventListener("click", () => { this.store.setLinkKind(id, k); this.closeMenu(); });
      row.appendChild(b);
    });
    kind.appendChild(row);
    const weekLabel = (w: string | null) => (w ? `KW ${Number(w.split("-W")[1])}` : "ohne KW");
    if (l.kind === 1) {
      const hint = l.conflict
        ? `Konflikt: Vorgänger in ${weekLabel(l.fromWeek)}, Nachfolger schon in ${weekLabel(l.toWeek)}`
        : `Vorgänger ${weekLabel(l.fromWeek)} → Nachfolger ${weekLabel(l.toWeek)}`;
      kind.appendChild(el("div", l.conflict ? "menu-hint conflict" : "menu-hint", hint));
    }
    const colors = section("Farbe");
    const grid = el("div", "swatches");
    LINK_COLORS.forEach((c, i) => {
      const b = el("button", "swatch");
      b.type = "button";
      b.title = c.name;
      b.setAttribute("aria-label", c.name);
      b.style.background = css(c.rgb);
      if (l.color === i) b.setAttribute("aria-pressed", "true");
      b.addEventListener("click", () => { this.store.setLinkStyle(id, i, l.dashed); this.closeMenu(); });
      grid.appendChild(b);
    });
    colors.appendChild(grid);
    const dashed = el("button", "menu-action", l.dashed ? "Durchgezogen" : "Gestrichelt");
    dashed.type = "button";
    dashed.addEventListener("click", () => { this.store.setLinkStyle(id, l.color, !l.dashed); this.closeMenu(); });
    menu.appendChild(dashed);
    const rev = el("button", "menu-action", "Richtung umkehren");
    rev.type = "button";
    rev.addEventListener("click", () => { this.store.reverseLink(id); this.closeMenu(); });
    menu.appendChild(rev);
    const del = el("button", "menu-action danger", "Verbindung löschen");
    del.type = "button";
    del.addEventListener("click", () => { this.store.deleteLinks([id]); this.selectedLink = null; this.closeMenu(); });
    menu.appendChild(del);
    this.placeMenu(menu, clientX, clientY);
  }

  private openFrameMenu(id: string, clientX: number, clientY: number) {
    const menu = $("#lw-menu");
    menu.replaceChildren();
    const f = this.store.frames().find((x) => x.id === id);
    const sec = el("div", "menu-section");
    sec.appendChild(el("div", "menu-title", "Rahmen"));
    if (f) sec.appendChild(el("div", "menu-hint", f.title || "Ohne Titel"));
    menu.appendChild(sec);
    const edit = el("button", "menu-action", "Rahmen bearbeiten …");
    edit.type = "button";
    edit.addEventListener("click", () => { this.closeMenu(); this.editFrame(id); });
    menu.appendChild(edit);
    const order = el("div", "menu-row");
    const toBtn = (label: string, front: boolean) => {
      const b = el("button", "chip plain", label);
      b.type = "button";
      b.title = front ? "Liegt über anderen Rahmen; Karten, die du dort ablegst, gehören zu diesem Rahmen" : "Liegt unter anderen Rahmen";
      b.addEventListener("click", () => { this.store.frameTo(id, front); this.closeMenu(); });
      order.appendChild(b);
    };
    toBtn("Nach vorne", true);
    toBtn("Nach hinten", false);
    menu.appendChild(order);
    const del = el("button", "menu-action danger", "Rahmen löschen (Karten bleiben)");
    del.type = "button";
    del.addEventListener("click", () => { this.store.deleteFrame(id); this.selectedFrame = null; this.closeMenu(); });
    menu.appendChild(del);
    menu.appendChild(el("div", "menu-hint", "Ziehen am Kopf verschiebt den Rahmen mit allen Karten. Doppelklick öffnet den Editor."));
    this.placeMenu(menu, clientX, clientY);
  }

  private placeMenu(menu: HTMLElement, clientX: number, clientY: number) {
    menu.hidden = false;
    const r = this.stage.getBoundingClientRect();
    const mw = 260, mh = menu.offsetHeight || 380;
    const x = Math.min(clientX - r.left, r.width - mw - 8);
    const y = Math.min(clientY - r.top, r.height - mh - 8);
    menu.style.transform = `translate(${Math.max(8, x)}px, ${Math.max(8, y)}px)`;
  }
}
